import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router-dom';
import useUser from '../hooks/useUser.js';
import GlobalContext from '../contexts/global.js';
import StateContext from '../contexts/state.js';
import { listInfo } from '../helpers/api/getPage.js';
import PageCard from './PageCard.js';
import RichText from './RichTextContent.js';
import TruncatedContent from './TruncatedContent.js';
import Loader from './Loader.js';
import CategoryPills from './CategoryPills.js';

import '../styles/listing.css';

const apiEndPoint = process.env.REACT_APP_FETCH_URL;
const DAY_MS = 24 * 60 * 60 * 1000;

const capitalize = (word) => word ? `${word[0].toUpperCase()}${word.slice(1)}` : "";

/* Keys of the selected options, none when a "show everything" option is selected */
const filterKeys = (selected = []) => selected.some(({ ignoreFilter }) => ignoreFilter) ? [] : selected.map(({ key }) => key);

/* Buckets pages into Today / Last 7 days / Earlier, dropping empty buckets so the first rendered group is always the first non-empty one */
const groupPagesByDate = (pages) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const sevenDaysAgo = new Date(startOfToday.getTime() - 7 * DAY_MS);

    const groups = [
        { key: "today", label: "Today", pages: [] },
        { key: "week", label: "Last 7 days", pages: [] },
        { key: "earlier", label: "Earlier", pages: [] }
    ];

    pages.forEach(page => {
        const publishDate = new Date(page.publishDate);
        const group = publishDate >= startOfToday ? groups[0] : publishDate >= sevenDaysAgo ? groups[1] : groups[2];
        group.pages.push(page);
    });

    return groups.filter(group => group.pages.length > 0);
};

const ListingCard = ({ page, badgeKeys, truncateLines }) => {
    const content = apiEndPoint ?
        (page.data?.content || "").replace(/(["'])\/api\//g, `$1${apiEndPoint}/api/`) :
        page.data?.content || "";

    const [expanded, setExpanded] = useState(false);
    const [overflows, setOverflows] = useState(false);

    return (
        <PageCard
            page={page}
            badgeKeys={badgeKeys}
            footerActions={ overflows && (
                <button
                    type="button"
                    className="action expand-toggle"
                    aria-expanded={expanded}
                    onClick={() => setExpanded(current => !current)}
                >
                    { expanded ? "Shrink post" : "Expand post" }
                    <img
                        className="action-icon"
                        src={`${process.env.PUBLIC_URL}/assets/icons/arrow.svg`}
                    />
                </button>
            ) }
        >
            <Link
                className="title"
                to={`/story/${page.urn}`}
            >
                { page.data?.title || "" }
            </Link>

            <TruncatedContent
                expanded={expanded}
                lines={truncateLines}
                onOverflowChange={setOverflows}
            >
                <RichText
                    className="content"
                    html={ content }
                />
            </TruncatedContent>
        </PageCard>
    );
};

const Listing = ({
    heading,
    viewAllLink = null,
    filterGroups = [],
    search = "",
    sort = null,
    order = "desc",
    initialCount = 3,
    paginateBy = 3,
    badgeKeys = [],
    filterBy = null,
    groupByDate = false,
    truncateLines = 4,
    excludeIndexes = [],
    onPagesChange = null,
    ready = true,
    showEmptyListing = true,
    columns = 1
}) => {
    const { logout } = useUser();
    const { globalContext } = useContext(GlobalContext);
    const { state, updateState } = useContext(StateContext);
    const isMounted = useRef(false);

    const [loading, setLoading] = useState(true);
    const [count, setCount] = useState(0);
    const [pages, setPages] = useState([]);

    const filterStateKey = filterBy ? `current${capitalize(filterBy)}` : null;
    const filterOptionsKey = filterBy ? `${filterBy}s` : null;
    const filterOptions = filterBy ? (globalContext?.[filterOptionsKey] || []) : [];
    const filterByKeys = filterBy ? filterKeys(state[filterStateKey]) : [];
    const currentLosKeys = filterKeys(state.currentLos);
    const userLosKeys = (state.userLos || []).map(({ key }) => key);

    const loadMore = async ({ reset = false } = {}) => {
        setLoading(true);
        try {
            const response = await listInfo({
                skip: reset ? 0 : pages.length,
                limit: reset ? initialCount : paginateBy,
                order,
                sort,
                includeContent: true,
                filterGroups: [
                    ...filterGroups,
                    ...(filterByKeys.length ? [{
                        key: filterBy,
                        value: filterByKeys
                    }] : []),
                    ...(filterBy !== "los" && currentLosKeys.length ? [{
                        key: "los",
                        value: [ ...currentLosKeys, "all" ]
                    }] : []),
                    /* Non-firmwide articles only show when they match one of the user's LoS */
                    [
                        { key: "firmwide", value: "true" },
                        ...(userLosKeys.length ? [{
                            key: "los",
                            value: [ ...userLosKeys, "all" ]
                        }] : [])
                    ],
                    ...(excludeIndexes.length ? [{
                        key: "index",
                        value: excludeIndexes,
                        prefix: "",
                        exclude: true
                    }] : [])
                ],
                search,
                logout
            });

            if(isMounted.current && response) {
                const nextPages = [
                    ...(!reset ? pages : []),
                    ...(response.pages || [])
                ];
                setCount(response.count || 0);
                setPages(nextPages);
                onPagesChange?.(nextPages);
            }
        }
        catch (error) {
            console.error(error);
        }
        isMounted.current && setLoading(false);
    };

    useEffect(() => {
        ready && loadMore({ reset: true });
    }, [
        JSON.stringify(filterGroups),
        JSON.stringify(excludeIndexes),
        ready,
        search,
        sort,
        order,
        filterByKeys.join(","),
        currentLosKeys.join(","),
        userLosKeys.join(",")
    ]);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, []);

    if(!showEmptyListing && !loading && pages.length === 0) {
        return null;
    }

    return (
        <div
            className="listing"
            style={{ "--listing-columns": columns }}
            {
                ...(excludeIndexes.length && {
                    "data-excluded-count": excludeIndexes.length,
                    "data-excluded-indexes": excludeIndexes.join(",")
                })
            }
        >
            {
                heading && !groupByDate && (
                    <div className="heading-row">
                        <h2 className="heading">{heading}</h2>
                        {
                            viewAllLink && (
                                <Link
                                    className="action view-all"
                                    to={viewAllLink}
                                >
                                    View all
                                    <img
                                        className="action-icon"
                                        src={`${process.env.PUBLIC_URL}/assets/icons/arrow.svg`}
                                    />
                                </Link>
                            )
                        }
                    </div>
                )
            }

            {
                filterBy && (
                    <div className="location-filter">
                        <span className="location-filter-label">Show stories for</span>
                        <CategoryPills
                            options={filterOptions.filter(({ ignoreFilter }) => !ignoreFilter)}
                            selected={filterByKeys}
                            showAllPill={true}
                            onChange={
                                keys => updateState({
                                    /* Nothing selected falls back to the "show everything" option, if there is one */
                                    [filterStateKey]: keys.length ?
                                        filterOptions.filter(({ key }) => keys.includes(key)) :
                                        filterOptions.filter(({ ignoreFilter }) => ignoreFilter)
                                })
                            }
                        />
                    </div>
                )
            }

            {
                !loading && pages.length === 0 && (
                    <p className="no-results">No results found.</p>
                )
            }

            {
                groupByDate ? (
                    groupPagesByDate(pages).map(group => (
                        <React.Fragment key={group.key}>
                            <div className="heading-row">
                                <h2 className="heading">{group.label}</h2>
                            </div>
                            <div className="cards">
                                {
                                    group.pages.map(page => (
                                        <ListingCard
                                            key={page.urn}
                                            page={page}
                                            badgeKeys={badgeKeys}
                                            truncateLines={truncateLines}
                                        />
                                    ))
                                }
                            </div>
                        </React.Fragment>
                    ))
                ) : (
                    <div className="cards">
                        {
                            pages.map(page => (
                                <ListingCard
                                    key={page.urn}
                                    page={page}
                                    badgeKeys={badgeKeys}
                                    truncateLines={truncateLines}
                                />
                            ))
                        }
                    </div>
                )
            }

            <Loader running={loading} />

            {
                pages.length < count && !loading && (
                    <div
                        className="load-more"
                        onClick={() => loadMore()}
                    >
                        Load more
                    </div>
                )
            }
        </div>
    );
};

export default Listing;
